<template>
  <!-- Price Window Name and Description -->
  <div class="row mb-4">
    <div class="col-md-6">
      <label for="priceWindowName" class="form-label">Price Window Name</label>
      <input
        type="text"
        :class="{ 'form-control': true, 'form-control-error': invalidName }"
        id="priceWindowName"
        v-model="nameValue"
        @blur="checkInvalidName"
      />
    </div>
    <div class="col-md-6">
      <label for="priceWindowDescription" class="form-label"
        >Price Window Description</label
      >
      <input
        type="text"
        class="form-control"
        id="priceWindowDescription"
        v-model="descriptionValue"
      />
    </div>
  </div>

  <!-- Reservation Days Range -->
  <div class="row mb-4 justify-content-md-center">
    <div class="col-md-8">
      <div class="d-flex align-items-center">
        <span for="daysRange">For reservations</span>
        <input
          type="number"
          min="0"
          max="9999"
          :class="{
            'form-control': true,
            'me-2 ': true,
            'daysRange-input': true,
            'form-control-error': invalidDaysIn || daysInMoreThanDayOut,
          }"
          id="daysRangeStart"
          v-model.number="daysInValue"
          placeholder="0"
          @blur="checkInvalidDaysIn"
        />
        <span class="me-2">to</span>
        <input
          type="number"
          min="0"
          max="9999"
          :class="{
            'form-control': true,
            'me-2 ': true,
            'daysRange-input': true,
            'form-control-error': invalidDaysOut || daysInMoreThanDayOut,
          }"
          id="daysRangeEnd"
          v-model.number="daysOutValue"
          placeholder="9999"
          @blur="checkInvalidDaysOut"
        />
        <span>days before a tee time, this pricing logic will be used.</span>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useToast } from 'vue-toastification';

export default defineComponent({
  name: 'PricingStrategyNameDaysComponent',
  emits: ['updateWindow'],
  props: {
    name: {
      type: String as PropType<string | undefined | null>,
      required: true,
    },
    description: {
      type: String as PropType<string | undefined | null>,
      required: true,
    },
    daysIn: {
      type: Number as PropType<number | undefined | null>,
      required: true,
    },
    daysOut: {
      type: Number as PropType<number | undefined | null>,
      required: true,
    },
    invalidDaysIn: {
      type: Boolean,
      required: true,
    },
    daysInMoreThanDayOut: {
      type: Boolean,
      required: true,
    },
    invalidDaysOut: {
      type: Boolean,
      required: true,
    },
    invalidName: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const nameValue = computed({
      get: () => props.name,
      set: (value: string) => {
        emit('updateWindow', 'name', value);
      },
    });
    const descriptionValue = computed({
      get: () => props.description,
      set: (value: string) => {
        emit('updateWindow', 'description', value);
      },
    });
    const daysInValue = computed({
      get: () => props.daysIn,
      set: (value: number) => {
        emit('updateWindow', 'daysIn', value);
      },
    });
    const daysOutValue = computed({
      get: () => props.daysOut,
      set: (value: number) => {
        emit('updateWindow', 'daysOut', value);
      },
    });
    const checkDaysInMoreThanDayOut = () => {
      if (props.daysInMoreThanDayOut) {
        toast.clear();
        toast.warning('Days in cannot be more that days out.');
      }
    };
    const checkInvalidDaysIn = () => {
      if (props.invalidDaysIn) {
        toast.clear();
        toast.warning('Pricing windows cannot overlap.');
      } else {
        checkDaysInMoreThanDayOut();
      }
    };
    const checkInvalidDaysOut = () => {
      if (props.invalidDaysOut) {
        toast.clear();
        toast.warning('Pricing windows cannot overlap.');
      } else {
        checkDaysInMoreThanDayOut();
      }
    };
    const checkInvalidName = () => {
      if (props.invalidName) {
        toast.clear();
        toast.warning('Price Window Name must be unique.');
      }
    };

    return {
      nameValue,
      descriptionValue,
      daysInValue,
      daysOutValue,
      checkInvalidDaysIn,
      checkInvalidDaysOut,
      checkDaysInMoreThanDayOut,
      checkInvalidName,
    };
  },
});
</script>
<style scoped>
strong {
  margin-right: 10px;
}

/* Add these new styles for consistency */
.form-control:focus,
.form-select:focus {
  border-color: #618183;
  box-shadow: 0 0 0 0.25rem rgba(64, 95, 107, 0.25);
}

.form-control-error {
  border-color: #c82333;
}

.price-display {
  display: flex;
  align-items: center;
  background-color: #98b2b4;
  color: white;
  margin-bottom: 0px !important;
}

.bi-info-circle {
  margin-left: 5px;
}

.daysRange-input {
  width: 90px;
  margin-left: 5px;
}
</style>
